import React from 'react';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';

// This component now delegates auth to a small backend service.
// The backend verifies the real password (from env) and sets an httpOnly cookie.

export default function MaintenanceGate({ children }) {
  const [authorized, setAuthorized] = React.useState(false);
  const [loading, setLoading] = React.useState(true);
  const [value, setValue] = React.useState('');
  const [show, setShow] = React.useState(false);
  const [error, setError] = React.useState('');

  React.useEffect(() => {
    let mounted = true;
    // Check cookie/token via backend
    fetch('/api/maintenance/check', { credentials: 'include' })
      .then((r) => r.json())
      .then((j) => {
        if (!mounted) return;
        setAuthorized(Boolean(j.authorized));
      })
      .catch(() => setAuthorized(false))
      .finally(() => mounted && setLoading(false));
    return () => {
      mounted = false;
    };
  }, []);

  const submit = async (e) => {
    e?.preventDefault();
    setError('');
    try {
      const res = await fetch('/api/maintenance/unlock', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password: value }),
      });
      if (res.ok) {
        setAuthorized(true);
      } else if (res.status === 401) {
        setError('Falsches Passwort.');
      } else {
        const json = await res.json().catch(() => ({}));
        setError(json.message || 'Serverfehler');
      }
    } catch (err) {
      setError('Netzwerkfehler');
    }
  };

  const reset = async () => {
    try {
      await fetch('/api/maintenance/logout', { method: 'POST', credentials: 'include' });
    } catch (e) {
      // ignore
    }
    setAuthorized(false);
    setValue('');
    setError('');
  };

  if (loading) {
    return null;
  }

  if (authorized) return children;

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'background.default', p: 2 }}>
      <Paper elevation={6} sx={{ width: '100%', maxWidth: 720, p: 4 }}>
        <Typography variant="h5" gutterBottom>
          Wartungsmodus
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          Die Website befindet sich derzeit im Wartungsmodus. Bitte gib das Passwort ein, um fortzufahren.
        </Typography>

        <Box component="form" onSubmit={submit} sx={{ display: 'flex', gap: 2, flexDirection: 'column' }}>
          <TextField
            autoFocus
            label="Passwort"
            type={show ? 'text' : 'password'}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            error={!!error}
            helperText={error || 'Gib das Passwort ein, um die Seite anzuzeigen.'}
            InputProps={{
              endAdornment: (
                <IconButton aria-label={show ? 'Hide password' : 'Show password'} onClick={() => setShow((s) => !s)} edge="end">
                  {show ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              ),
            }}
            fullWidth
          />

          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
            <Button variant="text" onClick={reset}>Zurücksetzen</Button>
            <Button variant="contained" onClick={submit}>Entriegeln</Button>
          </Box>
        </Box>
      </Paper>
    </Box>
  );
}
